import React, { useState, useEffect, useContext, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, ChevronDown, Check, User as UserIcon } from "lucide-react";

import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../ui/select";
import { cn } from "../../lib/utils";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import useQueues from "../../hooks/useQueues";
import useWhatsApps from "../../hooks/useWhatsApps";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Can } from "../Can";

const TransferTicketModal = ({ modalOpen, onClose, ticketid, ticketWhatsappId }) => {
	const navigate = useNavigate();
	const [users, setUsers] = useState([]);
	const [queues, setQueues] = useState([]);
	const [allQueues, setAllQueues] = useState([]);
	const [loading, setLoading] = useState(false);
	const [selectedUser, setSelectedUser] = useState(null);
	const [selectedQueue, setSelectedQueue] = useState("");
	const [selectedWhatsapp, setSelectedWhatsapp] = useState(ticketWhatsappId);
	const { findAll: findAllQueues } = useQueues();
	const { loadingWhatsapps, whatsApps } = useWhatsApps();

	const { user: loggedInUser } = useContext(AuthContext);

	useEffect(() => {
		const loadQueues = async () => {
			const list = await findAllQueues();
			setAllQueues(list);
			setQueues(list);
		};
		loadQueues();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (!modalOpen) return;
		const fetchUsers = async () => {
			try {
				const { data } = await api.get("/users/");
				setUsers(data.users || []);
			} catch (err) {
				toastError(err);
			}
		};
		fetchUsers();
	}, [modalOpen]);

	const handleClose = () => {
		onClose();
		setSelectedUser(null);
	};

	const handleSelectUser = (user) => {
		setSelectedUser(user);
		if (user != null && Array.isArray(user.queues) && user.queues.length > 0) {
			setQueues(user.queues);
		} else {
			setQueues(allQueues);
			setSelectedQueue("");
		}
	};

	const handleSaveTicket = async (e) => {
		e.preventDefault();
		if (!ticketid) return;
		setLoading(true);
		try {
			let data = {};

			if (selectedUser) {
				data.userId = selectedUser.id;
			}

			if (selectedQueue && selectedQueue !== null) {
				data.queueId = selectedQueue;

				if (!selectedUser) {
					data.status = "pending";
					data.userId = null;
				}
			}

			if (selectedWhatsapp) {
				data.whatsappId = selectedWhatsapp;
			}

			await api.put(`/tickets/${ticketid}`, data);

			setLoading(false);
			navigate(`/tickets`);
		} catch (err) {
			setLoading(false);
			toastError(err);
		}
	};

	return (
		<Dialog open={modalOpen} onOpenChange={(o) => !o && handleClose()}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>{i18n.t("transferTicketModal.title")}</DialogTitle>
				</DialogHeader>
				<form onSubmit={handleSaveTicket} className="space-y-4">
					<div className="space-y-1.5">
						<Label>{i18n.t("transferTicketModal.fieldLabel")}</Label>
						<Select
							value={selectedUser ? String(selectedUser.id) : "none"}
							onValueChange={(v) => {
								if (v === "none") {
									handleSelectUser(null);
								} else {
									const userObj = users.find((u) => String(u.id) === String(v));
									handleSelectUser(userObj || null);
								}
							}}
						>
							<SelectTrigger>
								<SelectValue placeholder={i18n.t("transferTicketModal.fieldLabel")} />
							</SelectTrigger>
							<SelectContent
								className={cn(
									users.length > 10 ? "max-h-60 overflow-y-auto" : "max-h-none overflow-y-visible"
								)}
							>
								<SelectItem value="none">Nenhum (Qualquer atendente)</SelectItem>
								{users.map((user) => (
									<SelectItem key={user.id} value={String(user.id)}>
										{user.name}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>

					<div className="space-y-1.5">
						<Label>{i18n.t("transferTicketModal.fieldQueueLabel")}</Label>
						<Select
							value={selectedQueue ? String(selectedQueue) : "none"}
							onValueChange={(v) => setSelectedQueue(v === "none" ? "" : v)}
						>
							<SelectTrigger>
								<SelectValue placeholder={i18n.t("transferTicketModal.fieldQueuePlaceholder")} />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="none">&nbsp;</SelectItem>
								{queues.map((queue) => (
									<SelectItem key={queue.id} value={String(queue.id)}>
										{queue.name}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>

					<Can
						role={loggedInUser.profile}
						perform="ticket-options:transferWhatsapp"
						yes={() =>
							!loadingWhatsapps && (
								<div className="space-y-1.5">
									<Label>{i18n.t("transferTicketModal.fieldConnectionLabel")}</Label>
									<Select
										value={selectedWhatsapp ? String(selectedWhatsapp) : undefined}
										onValueChange={(v) => setSelectedWhatsapp(v)}
									>
										<SelectTrigger>
											<SelectValue placeholder={i18n.t("transferTicketModal.fieldConnectionPlaceholder")} />
										</SelectTrigger>
										<SelectContent>
											{whatsApps.map((whasapp) => (
												<SelectItem key={whasapp.id} value={String(whasapp.id)}>
													{whasapp.name}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								</div>
							)
						}
					/>

					<DialogFooter>
						<Button type="button" variant="outline" onClick={handleClose} disabled={loading}>
							{i18n.t("transferTicketModal.buttons.cancel")}
						</Button>
						<Button type="submit" disabled={loading}>
							{loading && <Loader2 className="h-4 w-4 animate-spin" />}
							{i18n.t("transferTicketModal.buttons.ok")}
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
};

export default TransferTicketModal;
